'use client';

import { useEffect, useId, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Locale,
  LocaleValue,
  LocaleDetails,
  countryLanguages,
  getLanguageDisplayName,
  getRegionSpecificDefaultHref,
  supportedLocales,
} from 'shared/src/types/languages.type';
import { usePathname } from 'next/navigation';
import Cookies from 'js-cookie';
import { useUiContent, useRegionUiContent } from '@/components/ui-content-provider';
import { useCurrentShoppingRegion } from '@/hooks/use-current-shopping-region';
import { track } from '@/lib/tracking/tracker';
import { isAppHostname } from '@/lib/runtime/is-native-app';
import { resolveLanguageSwitchTargets } from './resolve-language-switch';
import { parseLanguageSwitchTarget, settleLanguageSwitchTarget } from './language-switch-target';

// A click the menu has not resolved yet waits this long for the answer, then
// goes to the twin URL as it always did.
const CLICK_RESOLVE_TIMEOUT_MS = 2_500;

/** A page path below a locale home: the only kind of language URL that can be missing. */
const needsTargetCheck = (url: unknown): url is string =>
  (parseLanguageSwitchTarget(url)?.slugs.length ?? 0) > 0;

type LanguageSelectorProps = {
  hide: boolean;
  locale: LocaleDetails;
  children?: React.ReactNode;
  href: LocaleValue;
  header?: boolean;
};

// Geometry of the anchored dropdown, in viewport pixels. Measured rather than
// expressed in CSS because the trigger sits in a sticky header whose height
// moves with the safe-area inset — a hardcoded `top` is wrong on notched
// phones and on any header tweak.
type PanelPosition = { top: number; right: number; width: number; maxHeight: number };

// NB: this project overrides `sm` to 840px (tailwind.config.js), it is not the
// Tailwind default 640.
const NARROW_BREAKPOINT = 840;

export const LanguageSelector = ({ locale, href, children, hide = false, header = false }: LanguageSelectorProps) => {
  const ui = useUiContent();
  const regionUi = useRegionUiContent();
  const [open, setOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);
  // Portal target is only available on the client; gate rendering until mounted.
  const [mounted, setMounted] = useState(false);
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const panelRef = useRef<null | HTMLDivElement>(null);
  const buttonRef = useRef<null | HTMLButtonElement>(null);
  const triggerId = useId();
  const parentHref = usePathname();
  const lang: Locale = locale?.path ?? 'en-CA';

  if (!href || Object.keys(href).length === 0) {
    const defaultHref = getRegionSpecificDefaultHref(locale);
    href = { ...defaultHref };
  }

  const filteredHref = Object.fromEntries(Object.entries(href).filter(([key]) => !key.includes('ALL')));

  const currentLocaleSupportedOrder = supportedLocales[lang] || [];

  const getLanguagePriority = (langCode: Locale): number => {
    const index = currentLocaleSupportedOrder.findIndex((localeDetails) => localeDetails.path === langCode);
    return index === -1 ? 999 : index;
  };

  const currentRegion = locale.regionCode;

  const pathSegments = parentHref.split('/').filter(Boolean);
  const isSingleSlug = pathSegments.length === 2;
  const singleSlugId = isSingleSlug ? pathSegments[1] : null;

  let regionSupportedLanguagesUnsorted: Record<string, string>;
  if (isSingleSlug && singleSlugId) {
    const regionLocales = (supportedLocales[lang] || []).filter((d) => d.regionCode === currentRegion);
    regionSupportedLanguagesUnsorted = Object.fromEntries(
      regionLocales.map((localeDetails) => {
        const localePath = localeDetails.path as Locale;
        const fromHref = href[localePath];
        const url = typeof fromHref === 'string' && fromHref.trim() !== '' ? fromHref : `/${localePath}/${singleSlugId}`;
        return [localePath, url];
      }),
    );
  } else {
    regionSupportedLanguagesUnsorted = Object.fromEntries(
      Object.entries(filteredHref).filter(([key, value]) => {
        const isInCurrentRegion = key.includes(currentRegion);
        const isSupported = currentLocaleSupportedOrder.some((localeDetails) => localeDetails.path === key);
        return isInCurrentRegion && isSupported && value != null;
      }),
    ) as Record<string, string>;
  }

  const regionSupportedLanguages = Object.fromEntries(
    Object.entries(regionSupportedLanguagesUnsorted).sort(
      ([a], [b]) => getLanguagePriority(a as Locale) - getLanguagePriority(b as Locale),
    ),
  );

  const otherAvailableLanguagesUnsorted = Object.fromEntries(
    Object.entries(filteredHref).filter(([key]) => !key.includes(currentRegion)),
  );

  const otherAvailableLanguages = Object.fromEntries(
    Object.entries(otherAvailableLanguagesUnsorted).sort(
      ([a], [b]) => getLanguagePriority(a as Locale) - getLanguagePriority(b as Locale),
    ),
  );

  // `href` promises a twin in every language, but a twin can be missing: its
  // mapping outlived its document, and the URL 404s. Each language URL is
  // resolved (twin, else nearest live ancestor, else locale home) once, when
  // the menu opens or on a click it has not answered yet. Never on render.
  const [resolvedTargets, setResolvedTargets] = useState<Record<string, string>>({});
  const pendingTargetsRef = useRef(new Map<string, Promise<string>>());
  const resolveTargets = useCallback((urls: string[]): Promise<string>[] => {
    const pending = pendingTargetsRef.current;
    const fresh = Array.from(new Set(urls.filter((url) => !pending.has(url))));
    if (fresh.length > 0) {
      const call = resolveLanguageSwitchTargets(fresh).catch(() => ({}) as Record<string, string>);
      for (const url of fresh) {
        pending.set(
          url,
          call.then((answers) => answers[url] ?? url),
        );
      }
      void call.then((answers) => {
        if (Object.keys(answers).length > 0) {
          setResolvedTargets((previous) => ({ ...previous, ...answers }));
        } else {
          // Nothing came back (the call failed): let a later open or click ask again.
          for (const url of fresh) pending.delete(url);
        }
      });
    }
    return urls.map((url) => pending.get(url) ?? Promise.resolve(url));
  }, []);

  // The region's other languages are the ones a shopper picks; the long
  // cross-region list is resolved per click only. A locale home always
  // answers, so it is never asked about.
  const regionTargetsKey = Object.entries(regionSupportedLanguages)
    .filter(([code, url]) => !lang.includes(code) && needsTargetCheck(url))
    .map(([, url]) => url)
    .join('\n');

  useEffect(() => {
    if (!open || !regionTargetsKey) return;
    resolveTargets(regionTargetsKey.split('\n'));
  }, [open, regionTargetsKey, resolveTargets]);

  const close = useCallback(() => {
    setOpen(false);
    setShowMore(false);
  }, []);

  // Right-aligned to the header's inner edge, hanging just under the bar. Same
  // shape on every breakpoint: a menu, never a full-screen surface.
  const place = useCallback(() => {
    const trigger = buttonRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    // Hang off the bar's bottom edge, not the trigger's — the header pads
    // below its buttons, so the trigger alone would tuck the panel under the
    // bar's border.
    const bar = trigger.closest('[data-top-bar]')?.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight;
    const isNarrow = viewportWidth < NARROW_BREAKPOINT;
    const edge = isNarrow ? 12 : 16;
    const gap = 8;

    const width = isNarrow ? Math.min(272, viewportWidth - edge * 2) : 256;
    const top = Math.round(Math.max(rect.bottom, bar?.bottom ?? 0) + gap);
    // Cap at 70% of the viewport so the expanded "other languages" list stays a
    // menu with page visible behind it, instead of growing into a full screen.
    const maxHeight = Math.max(160, Math.min(viewportHeight - top - edge, Math.round(viewportHeight * 0.7)));

    // Scroll fires this constantly and the sticky header rarely moves — keep
    // the previous object when nothing changed so it does not re-render.
    setPosition((previous) =>
      previous &&
      previous.top === top &&
      previous.right === edge &&
      previous.width === width &&
      previous.maxHeight === maxHeight
        ? previous
        : { top, right: edge, width, maxHeight },
    );
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Keep the anchor true while open — the header is sticky, but rotation, a
  // resize or an address-bar collapse all move the trigger.
  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', place);
    window.addEventListener('orientationchange', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('orientationchange', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  useEffect(() => {
    // Persist the page's locale as the real `locale` preference cookie (the
    // old write here was a 2-letter `lang` cookie nothing reads). Durable so
    // the middleware's first-visit geo redirect never overrides a locale the
    // user is actually browsing in.
    Cookies.set('locale', locale.path, {
      expires: 365,
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'lax',
    });
    if (hide) {
      localStorage.getItem('prevPath') ?? removeLastSegment(parentHref);
    } else if (href[lang] === parentHref) {
      localStorage.setItem('prevPath', parentHref);
    }
  }, [hide, parentHref, lang, href, locale.path]);

  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      if (
        panelRef.current &&
        buttonRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        close();
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
    }

    if (open) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [open, close]);

  const getLanguageUrl = (targetLocale: Locale, targetUrl: string): string => {
    if (typeof window === 'undefined') return targetUrl;
    const currentDomain = window.location.hostname;
    // Native-app origin serves every locale itself — never switch domains.
    // ⚠ Must be checked before the .includes() branches below:
    // "app.inceptionia.com".includes("inceptionia.com") is true.
    if (isAppHostname(currentDomain)) return targetUrl;
    const currentLocale = locale.path as Locale;

    if (
      (currentLocale.includes('CA') && targetLocale.includes('US')) ||
      (currentLocale.includes('US') && targetLocale.includes('CA'))
    ) {
      if (targetLocale.includes('US') && currentDomain.includes('inceptionai.ca')) {
        const newUrl = targetUrl.replace(/\/(en|fr)-CA/g, (_match, lang) => `/${lang}-US`);
        return `https://inceptionia.com${newUrl}`;
      }
      if (
        targetLocale.includes('CA') &&
        (currentDomain.includes('inceptionia.com') ||
          currentDomain.includes('inceptionai.shop') ||
          currentDomain.includes('inceptionaiapp.com'))
      ) {
        const newUrl = targetUrl.replace(/\/(en|fr)-US/g, (_match, lang) => `/${lang}-CA`);
        return `https://www.inceptionai.ca${newUrl}`;
      }
    }
    return targetUrl;
  };

  const needsDomainSwitch = (targetLocale: Locale): boolean => {
    if (typeof window === 'undefined') return false;
    const currentDomain = window.location.hostname;
    if (isAppHostname(currentDomain)) return false;
    const currentLocale = locale.path as Locale;
    return (
      (currentLocale.includes('CA') && targetLocale.includes('US') && currentDomain.includes('inceptionai.ca')) ||
      (currentLocale.includes('US') &&
        targetLocale.includes('CA') &&
        (currentDomain.includes('inceptionia.com') ||
          currentDomain.includes('inceptionai.shop') ||
          currentDomain.includes('inceptionaiapp.com')))
    );
  };

  const handleLanguageSwitch = (targetLocale: Locale, targetUrl: string) => {
    close();
    window.location.href = getLanguageUrl(targetLocale, targetUrl);
  };

  const hasOtherLanguages = Object.keys(otherAvailableLanguages).length > 0;

  // "Canada · Québec · Trois-Rivières" under the Change region row, so the menu
  // answers "where am I shopping?" and not only "how do I change it?". Null
  // whenever no subregion is stored — the row then looks exactly as it always
  // has. Same hook as the header badge, so the two can never disagree.
  const shoppingRegion = useCurrentShoppingRegion(locale.regionCode, locale.path);
  const currentRegionLine = shoppingRegion
    ? [shoppingRegion.countryName, shoppingRegion.name, shoppingRegion.city]
        .filter((part): part is string => Boolean(part))
        .join(' · ')
    : null;

  const LanguageItem = ({ langCode, url, showRegionTag }: { langCode: Locale; url: string; showRegionTag?: boolean }) => {
    const isActive = lang.includes(langCode);
    const destination = resolvedTargets[url] ?? url;
    return (
      <a
        href={getLanguageUrl(langCode, destination)}
        className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 no-underline transition-colors duration-150
          ${isActive
            ? 'bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400'
            : 'text-neutral-700 hover:bg-neutral-100 dark:text-slate-300 dark:hover:bg-white/10'
          }`}
        onClick={(e) => {
          track('language_switch', { from: lang, to: langCode });
          const opensElsewhere = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
          if (!isActive && !opensElsewhere && resolvedTargets[url] === undefined && needsTargetCheck(url)) {
            // Not answered yet: find out where this language actually answers
            // before leaving, so a missing twin never lands on a 404.
            e.preventDefault();
            void settleLanguageSwitchTarget(
              url,
              (target) => resolveTargets([target])[0],
              CLICK_RESOLVE_TIMEOUT_MS,
            ).then((resolved) => handleLanguageSwitch(langCode, resolved));
            return;
          }
          if (needsDomainSwitch(langCode)) {
            e.preventDefault();
            handleLanguageSwitch(langCode, destination);
          }
        }}
      >
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-150
            ${isActive
              ? 'border-sky-500 dark:border-sky-400'
              : 'border-neutral-300 group-hover:border-neutral-400 dark:border-slate-500 dark:group-hover:border-slate-400'
            }`}
        >
          {isActive && <span className="h-2.5 w-2.5 rounded-full bg-sky-500 dark:bg-sky-400" />}
        </span>
        <span className="flex-1 text-sm font-medium">{getLanguageDisplayName(countryLanguages[langCode])}</span>
        {showRegionTag && (
          <span className="rounded-md bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-500 dark:bg-white/10 dark:text-slate-400">
            {langCode.split('-')[1]}
          </span>
        )}
      </a>
    );
  };

  return (
    <div className="z-50 flex items-center">
      <button
        ref={buttonRef}
        id={triggerId}
        className="static float-none m-0 flex items-center p-0 max-sm:mx-1 lg:ml-2"
        onClick={() => {
          // Measure before the panel becomes visible, in the same batch, so it
          // never paints one frame at a stale position.
          if (!open) place();
          setOpen(!open);
        }}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {children}
      </button>

      {/*
        Rendered through a portal to <body> so the panel escapes the sticky
        header's stacking context (the header establishes one via z-50 +
        backdrop-blur + translate3d, which would otherwise cap the panel's
        effective z-index and let page overlays paint over it).
      */}
      {mounted &&
        createPortal(
          <>
            {/*
              Invisible click-catcher on touch widths: the first tap outside
              dismisses the menu instead of activating whatever it landed on.
              No dim and no blur — this is a menu, not a modal, and the page
              behind it stays legible.
            */}
            <div
              className={`fixed inset-0 z-[390] sm:hidden ${open ? '' : 'pointer-events-none'}`}
              onClick={close}
              aria-hidden="true"
            />

            {/*
              One layout at every breakpoint: a dropdown anchored under the
              trigger, sized to its content and capped by `maxHeight`, with the
              list scrolling inside that cap. Position comes from `place()` —
              see PanelPosition for why it is measured and not CSS.

              The panel blurs its own backdrop: the dark backgrounds are
              translucent and there is no dimmed overlay behind them anymore.
            */}
            <div
              ref={panelRef}
              role="dialog"
              aria-labelledby={triggerId}
              style={position ?? undefined}
              className={`fixed right-3 top-16 z-[400] w-64 max-h-[70dvh]
                flex flex-col
                rounded-2xl border border-neutral-200/80 bg-white shadow-xl
                dark:border-transparent-dark dark:shadow-2xl backdrop-blur-xl
                ${header ? 'dark:bg-dark/45' : 'dark:bg-dark/90'}
                origin-top-right transition-all duration-200
                ${open
                  ? 'visible translate-y-0 scale-100 opacity-100'
                  : 'invisible -translate-y-1 scale-95 opacity-0'
                }`}
            >
              {/* Panel content — the only part that scrolls */}
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                {/* Region languages */}
                <div className="flex flex-col gap-0.5 p-2">
                  {Object.keys(regionSupportedLanguages).map((key) => {
                    const langCode = key as Locale;
                    const url = regionSupportedLanguages[langCode] || '/';
                    return <LanguageItem key={key} langCode={langCode} url={url} />;
                  })}
                </div>

                {/* Expand toggle for other languages */}
                {hasOtherLanguages && (
                  <>
                    <div className="px-3">
                      <div className="border-t border-neutral-200/60 dark:border-slate-700/40" />
                    </div>
                    <div className="p-2">
                      <button
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium text-neutral-500 transition-colors duration-150 hover:bg-neutral-100 hover:text-neutral-700 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-slate-300"
                        onClick={() => setShowMore(!showMore)}
                        aria-expanded={showMore}
                      >
                        <svg
                          className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${showMore ? 'rotate-180' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                        </svg>
                        <span>
                          {showMore
                            ? ui.showLess
                            : ui.showMoreLanguages}
                        </span>
                      </button>

                      {/*
                        Reveal cap has to clear the tallest possible list (every
                        cross-region locale). A viewport-relative cap clipped the
                        tail unreachably, since this box is `overflow-hidden` and
                        the scroller is its ancestor.
                      */}
                      <div
                        className={`overflow-hidden transition-all duration-200 ease-in-out
                          ${showMore ? 'mt-1 max-h-[1500px] opacity-100' : 'max-h-0 opacity-0'}`}
                      >
                        <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-slate-500">
                          {ui.otherLanguages}
                        </div>
                        <div className="flex flex-col gap-0.5">
                          {Object.keys(otherAvailableLanguages).map((key) => {
                            const langCode = key as Locale;
                            const url = otherAvailableLanguages[langCode] || '/';
                            return <LanguageItem key={key} langCode={langCode} url={url} showRegionTag />;
                          })}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Change region footer — pinned, never pushed by the scroller */}
              <div className="shrink-0 border-t border-neutral-200/60 dark:border-slate-700/40">
                <button
                  className="flex w-full items-center justify-between gap-3 rounded-b-2xl px-4 py-3 text-left text-sm font-medium text-neutral-600 transition-colors duration-150 hover:bg-neutral-50 dark:text-slate-300 dark:hover:bg-white/10"
                  onClick={() => {
                    window.location.href = `/${locale.path}/region?returnUrl=${encodeURIComponent(parentHref)}`;
                  }}
                >
                  {/* min-w-0 is what lets the truncate below actually clip
                      instead of forcing the panel wider than its measured width. */}
                  <span className="min-w-0 flex-1">
                    <span className="block">{ui.changeRegion}</span>
                    {currentRegionLine && (
                      <span className="mt-0.5 block truncate text-xs font-normal text-neutral-400 dark:text-slate-500">
                        {currentRegionLine}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-base">{regionUi.earthEmoji}</span>
                </button>
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};

const removeLastSegment = (href: string) => {
  let segments = href.split('/');
  if (segments.length > 0 && segments[segments.length - 1] === '') {
    segments.pop();
  }
  segments.pop();
  return segments.join('/');
};
